import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import CampaignsMenu from "../../components/campaignsMenu/CampaignsMenu";
import CreateCustomerListModal from "../../components/createCustomerListModal/CreateCustomerListModal";
import Loading from "../../components/loading/Loading";
import Pagination from "../../components/pagination/Pagination";
import { useTranslator } from "../../context/translator";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";
import { getCampaignListService } from "services/campaignList/services";
import { getCustomerCategoriesService } from "../../services/categories/services";
import { PERMISSION_CODE } from "utils/enum/enums";
import { Link } from "react-router-dom";

const CheckIcon = () => (
  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
    <svg
      width="13"
      height="13"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  </span>
);

const XIcon = () => (
  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500">
    <svg
      width="11"
      height="11"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  </span>
);

const EmptyState = ({
  translate,
  customersList,
  setCustomersList,
  getCustomerLists,
  categories,
}) => (
  <tr>
    <td colSpan={7}>
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
        <div className="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-4">
          <svg
            width="32"
            height="32"
            fill="none"
            viewBox="0 0 24 24"
            stroke="#1a56db"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H4v-2a4 4 0 014-4h1m4-4a4 4 0 110-8 4 4 0 010 8zm6 4a2 2 0 100-4 2 2 0 000 4zM3 18a2 2 0 100-4 2 2 0 000 4z"
            />
          </svg>
        </div>
        <p className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-1">
          No lists yet
        </p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mb-6 max-w-xs">
          Create your first customer list to start segmenting your campaigns.
        </p>
        <CreateCustomerListModal
          customersList={customersList}
          setCustomersList={setCustomersList}
          getCustomerLists={getCustomerLists}
          categories={categories}
          key="empty-create"
        />
      </div>
    </td>
  </tr>
);

const CampaignCustomers = () => {
  const { user } = useSelector((state) => state.user);

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    { skip: !userProfile?.user?.companyId },
  );

  const [customersList, setCustomersList] = useState({
    name: "",
    withEmail: true,
    withPhone: true,
    category: "ALL",
    country: { id: 0, name: "No Selected" },
    status: "ACTIVE",
    companyId: company?._id,
    allCustomer: true,
  });

  const [categories, setCategories] = useState([]);
  const [customerListsData, setCustomerListsData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageNumber, setPageNumber] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCustomerLists, setTotalCustomerLists] = useState(0);

  const { translate } = useTranslator();
  const [search] = useState("");

  useEffect(() => {
    if (company?._id) {
      setCustomersList((prev) => ({ ...prev, companyId: company._id }));
    }
  }, [company?._id]);

  const getCategories = useCallback(async () => {
    setIsLoading(true);
    if (!company?._id) {
      setIsLoading(false);
      return;
    }

    const data = await getCustomerCategoriesService({
      companyId: company?._id,
    });
    setIsLoading(false);

    if (!data || data?.code === PERMISSION_CODE.PERMISSION_DENIED) {
      setCategories([]);
      return;
    }

    setCategories(
      [{ _id: "0", name: translate("all"), key: "000" }, ...data?.categories] ??
        [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?._id, userProfile?.user]);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  const getCustomerLists = useCallback(async () => {
    if (!company?._id) return;
    setIsLoading(true);
    try {
      const data = await getCampaignListService({
        page,
        search,
        companyId: company._id,
        pageSize: 5,
      });

      setCustomerListsData(data?.campaignLists ?? []);
      const count = data?.totalCampaignList ?? 0;
      setTotalCustomerLists(count);
      setPageNumber(Math.ceil(count / 5));
    } finally {
      setIsLoading(false);
    }
  }, [company?._id, page, search]);

  useEffect(() => {
    getCustomerLists();
  }, [getCustomerLists]);

  if (userLoading || companyLoading) return null;

  const getStatusBadge = (status) => {
    const isActive = status === "ACTIVE";
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold
        ${
          isActive
            ? "bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-700/50"
            : "bg-gray-100 text-gray-500 ring-1 ring-gray-200 dark:bg-gray-700/50 dark:text-gray-400 dark:ring-gray-600/50"
        }`}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-blue-600 dark:bg-blue-400" : "bg-gray-400 dark:bg-gray-500"}`}
        />
        {isActive ? translate("active") : translate("deactivate")}
      </span>
    );
  };

  const columns = [
    translate("name"),
    translate("category"),
    translate("with-email"),
    translate("with-phone"),
    translate("all-customers"),
    translate("status"),
    translate("customers"),
    translate("actions"),
  ];

  return (
    <CampaignsMenu>
      <div className="max-w-6xl mx-auto p-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-1 h-10 rounded-full bg-blue-700 dark:bg-blue-500" />
            <div>
              <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-widest mb-0.5">
                {translate("campaigns")}
              </p>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
                {translate("pre-listed-customers")}
              </h2>
            </div>
            {totalCustomerLists > 0 && (
              <span className="ml-1 px-2.5 py-0.5 rounded-full bg-blue-700 dark:bg-blue-600 text-white text-xs font-bold">
                {totalCustomerLists}
              </span>
            )}
          </div>

          <CreateCustomerListModal
            customersList={customersList}
            setCustomersList={setCustomersList}
            getCustomerLists={getCustomerLists}
            categories={categories}
            key="create-customer-list-modal"
          />
        </div>

        {/* Table Card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm ring-1 ring-gray-100 dark:ring-gray-700/60 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-blue-700 dark:bg-blue-800">
                {columns.map((col, i) => (
                  <th
                    key={col}
                    className={`px-4 py-3.5 text-xs font-semibold text-blue-100 uppercase tracking-wider
                      ${i >= 2 && i <= 4 ? "text-center" : i >= 5 ? "text-center" : "text-left"}`}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-12">
                    <Loading />
                  </td>
                </tr>
              ) : customerListsData?.length > 0 ? (
                customerListsData.map((list) => (
                  <tr
                    key={list._id}
                    className="group relative hover:bg-blue-50/40 dark:hover:bg-blue-900/20 transition-colors duration-150"
                  >
                    {/* Name */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <span className="w-0.5 h-5 rounded-full bg-blue-700 dark:bg-blue-400 opacity-0 group-hover:opacity-100 transition-opacity -ml-1" />
                        <span className="font-semibold text-gray-800 dark:text-gray-100 text-sm">
                          {list.name}
                        </span>
                      </div>
                    </td>

                    {/* Category */}
                    <td className="px-4 py-3.5">
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {categories.find((cat) => cat._id === list.category)
                          ?.name ?? "—"}
                      </span>
                    </td>

                    <td className="px-4 py-3.5 text-center">
                      {list.withEmail ? <CheckIcon /> : <XIcon />}
                    </td>

                    <td className="px-4 py-3.5 text-center">
                      {list.withPhone ? <CheckIcon /> : <XIcon />}
                    </td>

                    <td className="px-4 py-3.5 text-center">
                      {list.allCustomer ? <CheckIcon /> : <XIcon />}
                    </td>

                    <td className="px-4 py-3.5 text-center">
                      {getStatusBadge(list.status)}
                    </td>

                    <td className="px-4 py-3.5 text-center text-sm text-gray-700 dark:text-gray-300">
                      {list.customerCount ?? 0}
                    </td>

                    <td className="px-4 py-3.5 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <CreateCustomerListModal
                          customersList={list}
                          setCustomersList={setCustomersList}
                          buttonName="edit"
                          getCustomerLists={getCustomerLists}
                          categories={categories}
                          key={`edit-${list._id}`}
                        />
                        <Link
                          to={`/campaigns/lists/${list._id}`}
                          className="ml-2 text-sm text-blue-700 hover:text-blue-900 bg-blue-500/10 px-3 py-1 rounded-md transition-colors duration-150
                            dark:text-blue-400 dark:hover:text-blue-300 dark:bg-blue-500/10 dark:hover:bg-blue-500/20"
                        >
                          {translate("view-customers")}
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <EmptyState
                  translate={translate}
                  customersList={customersList}
                  setCustomersList={setCustomersList}
                  getCustomerLists={getCustomerLists}
                  categories={categories}
                />
              )}
            </tbody>
          </table>

          {/* Footer with pagination */}
          {!isLoading && customerListsData?.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/40">
              <Pagination
                key="paginationCustomerLists"
                data={customerListsData}
                page={page}
                pageNumber={pageNumber}
                isLoading={isLoading}
                setPage={setPage}
                totalRecords={totalCustomerLists}
              />
            </div>
          )}
        </div>
      </div>
    </CampaignsMenu>
  );
};

export default CampaignCustomers;
